<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div class="d-flex align-items-center gap-3">
                <router-link :to="{ name: 'Production In Progress' }" class="btn btn-outline-secondary btn-sm">
                  <i class="fas fa-arrow-left me-1"></i> Volver
                </router-link>
                <h6 class="mb-0"><i class="fas fa-hammer me-1 text-success"></i> Producción — Pedido #{{ remissionId }}</h6>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
              <p class="mt-2 text-sm text-secondary">Cargando pedido...</p>
            </div>

            <div v-else-if="remission" class="mb-2">
              <div class="row">
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Cliente</label>
                  <p class="text-sm font-weight-bold mb-0">{{ remission.name }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Teléfono</label>
                  <p class="text-sm mb-0">{{ remission.phone || '—' }}</p>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Estado</label><br>
                  <span :class="['badge badge-sm', preparadoBadgeClass(remission.preparado)]">
                    {{ preparadoLabel(remission.preparado) }}
                  </span>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label text-xs">Fecha</label>
                  <p class="text-sm mb-0">{{ formatDate(remission.created_at) }}</p>
                </div>
              </div>
              <div class="d-flex gap-2 flex-wrap mt-3">
                <span class="badge bg-warning text-dark">Productos: {{ details.length }}</span>
                <span class="badge bg-secondary">Pares: {{ totalUnits }}</span>
                <span class="badge bg-info text-dark">Tareas: {{ completedAssignments }}/{{ totalAssignments }}</span>
                <span class="badge bg-success">Total a pagar: {{ money(totalPay) }}</span>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="!loading && details.length > 0" class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width: 40px"></th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ref</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Talla</th>
                    <th class="text-center text-secondary text-xxs font-weight-bolder opacity-7">Color</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Código de barras</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cant / Stock</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Asignado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Tareas / Pago</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="item in details" :key="item.id">
                    <tr>
                      <td class="text-center align-middle">
                        <button
                          type="button"
                          class="btn btn-sm btn-link p-0 m-0"
                          :title="expanded[item.id] ? 'Ocultar tareas' : 'Ver tareas'"
                          @click="toggleExpand(item.id)"
                        >
                          <i class="fas" :class="expanded[item.id] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>
                      </td>
                      <td class="align-middle">
                        <h6 class="mb-0 text-sm">{{ item.inventory?.name || 'Producto' }}</h6>
                      </td>
                      <td class="text-center align-middle">
                        <span class="text-xs text-secondary">{{ item.inventory?.ref || '—' }}</span>
                      </td>
                      <td class="text-center align-middle">
                        <span class="text-xs text-secondary">{{ itemSize(item) }}</span>
                      </td>
                      <td class="text-center align-middle">
                        <span class="text-xs text-secondary">{{ itemColor(item) }}</span>
                      </td>
                      <td class="text-center align-middle">
                        <span class="text-xs text-secondary">{{ itemBarcode(item) }}</span>
                      </td>
                      <td class="text-center align-middle">
                        <span class="text-sm font-weight-bold">{{ item.quantity }}</span> /
                        <span class="badge badge-sm" :class="itemStock(item) <= 0 ? 'bg-danger' : (itemStock(item) < item.quantity ? 'bg-warning text-dark' : 'bg-success')">
                          {{ itemStock(item) }}
                        </span>
                      </td>
                      <td class="text-center align-middle">
                        <select
                          :value="itemManufacturingStatus(item)"
                          class="status-select"
                          :class="statusPillClass(item)"
                          @change="onManufacturingStatusChange($event, item)"
                        >
                          <option value="pending">Pendiente</option>
                          <option value="in_progress">En proceso</option>
                          <option value="completed">Fabricado</option>
                        </select>
                      </td>
                      <td class="text-center align-middle">
                        <span class="text-xs font-weight-bold" :class="assignedQty(item) > parseFloat(item.quantity) ? 'text-danger' : ''">
                          {{ assignedQty(item) }}/{{ item.quantity }}
                        </span>
                      </td>
                      <td class="text-center align-middle">
                        <span class="text-xs">{{ completedOf(item) }}/{{ itemAssignments(item).length }}</span>
                        <span class="text-xs font-weight-bold ms-1">{{ money(itemPay(item)) }}</span>
                      </td>
                      <td class="text-center align-middle">
                        <button
                          type="button"
                          class="btn btn-success btn-sm mb-0"
                          title="Asignar usuario a este producto"
                          @click="openAssignModal(item)"
                        >
                          <i class="fas fa-user-plus me-1"></i> Asignar
                        </button>
                      </td>
                    </tr>
                    <tr v-if="expanded[item.id]" class="assignment-row">
                      <td :colspan="11" class="p-0">
                        <div class="assignment-box">
                          <div class="d-flex justify-content-between align-items-center px-3 py-2">
                            <span class="text-xs font-weight-bold text-secondary">
                              Tareas asignadas — {{ item.inventory?.name || 'Producto' }}
                            </span>
                          </div>
                          <div v-if="itemAssignments(item).length === 0" class="px-3 pb-3">
                            <p class="text-xs text-secondary mb-0">No hay tareas asignadas para este producto.</p>
                          </div>
                          <div v-else class="table-responsive">
                            <table class="table table-sm mb-2">
                              <thead>
                                <tr>
                                  <th class="text-xs">Usuario</th>
                                  <th class="text-xs">Oficio</th>
                                  <th class="text-center text-xs">Cant</th>
                                  <th class="text-center text-xs">Precio</th>
                                  <th class="text-center text-xs">Total</th>
                                  <th class="text-center text-xs">Asignada</th>
                                  <th class="text-center text-xs">Termina</th>
                                  <th class="text-center text-xs">Completada</th>
                                  <th class="text-xs">Asignó</th>
                                  <th class="text-center text-xs">Estado</th>
                                  <th class="text-center text-xs">Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="a in itemAssignments(item)" :key="a.id">
                                  <td class="text-xs font-weight-bold">{{ a.user?.name || '—' }}</td>
                                  <td class="text-xs">
                                    <span class="badge badge-sm bg-gradient-primary">{{ a.oficio?.name || 'Sin oficio' }}</span>
                                  </td>
                                  <td class="text-center text-xs">{{ a.quantity }}</td>
                                  <td class="text-center text-xs">{{ money(a.price) }}</td>
                                  <td class="text-center text-xs font-weight-bold">{{ money(a.total) }}</td>
                                  <td class="text-center text-xs">{{ shortDate(a.assigned_at) }}</td>
                                  <td class="text-center text-xs">{{ shortDate(a.estimated_at) }}</td>
                                  <td class="text-center text-xs">{{ shortDate(a.completed_at) }}</td>
                                  <td class="text-xs">{{ a.assigner?.name || '—' }}</td>
                                  <td class="text-center">
                                    <select
                                      :value="a.status"
                                      class="status-select"
                                      :class="assignmentPillClass(a)"
                                      @change="onAssignmentStatusChange($event, a)"
                                    >
                                      <option value="pending">Pendiente</option>
                                      <option value="in_progress">En proceso</option>
                                      <option value="completed">Completada</option>
                                    </select>
                                  </td>
                                  <td class="text-center">
                                    <button
                                      v-if="a.status !== 'completed'"
                                      type="button"
                                      class="btn btn-success btn-sm mb-0 me-1"
                                      title="Marcar como completada"
                                      @click="completeAssignment(a)"
                                    >
                                      <i class="fas fa-check-circle"></i>
                                    </button>
                                    <button type="button" class="btn btn-outline-warning btn-sm mb-0 me-1" title="Editar" @click="openEditModal(item, a)">
                                      <i class="fas fa-pen"></i>
                                    </button>
                                    <button type="button" class="btn btn-outline-danger btn-sm mb-0" title="Eliminar" @click="deleteAssignment(a)">
                                      <i class="fas fa-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <div v-else-if="!loading" class="text-center py-4">
              <p class="text-secondary text-sm">No hay productos en este pedido.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Asignar / Editar tarea -->
    <div v-if="showAssignModal" class="picking-modal-overlay" @click.self="closeAssignModal">
      <div class="picking-modal-box card shadow" style="max-width: 620px;">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">
              {{ editingId ? 'Editar tarea' : 'Asignar tarea' }} — {{ modalItem?.inventory?.name || 'Producto' }}
            </h6>
            <button type="button" class="btn-close" @click="closeAssignModal"></button>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-control-label text-xs">Oficio *</label>
              <select v-model="form.oficio_id" class="form-select form-select-sm" @change="onOficioChange">
                <option :value="null">Seleccionar oficio</option>
                <option v-for="o in oficios" :key="o.id" :value="o.id">{{ o.name }}</option>
              </select>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-control-label text-xs">Usuario *</label>
              <select v-model="form.user_id" class="form-select form-select-sm" :disabled="!form.oficio_id">
                <option :value="null">{{ form.oficio_id ? 'Seleccionar usuario' : 'Seleccione primero el oficio' }}</option>
                <option v-for="u in filteredUsers" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
              <small v-if="form.oficio_id && filteredUsers.length === 0" class="text-xs text-danger d-block mt-1">
                No hay usuarios con este oficio. Asígnalo en el módulo Usuarios (campo Procesos).
              </small>
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-control-label text-xs">Cantidad *</label>
              <input v-model.number="form.quantity" type="number" min="0.01" step="1" class="form-control form-control-sm" />
              <small class="text-xs text-secondary">Disponible: {{ modalRemaining }}</small>
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-control-label text-xs">Precio por unidad *</label>
              <input v-model.number="form.price" type="number" min="0" step="50" class="form-control form-control-sm" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-control-label text-xs">Total a pagar</label>
              <input :value="money(formTotal)" type="text" readonly class="form-control form-control-sm bg-light" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-control-label text-xs">Fecha de asignación</label>
              <input v-model="form.assigned_at" type="date" class="form-control form-control-sm" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-control-label text-xs">Fecha de terminación</label>
              <input v-model="form.estimated_at" type="date" class="form-control form-control-sm" />
            </div>
            <div class="col-md-12 mb-3">
              <label class="form-control-label text-xs">Observación</label>
              <textarea v-model="form.observation" rows="2" class="form-control form-control-sm"></textarea>
            </div>
          </div>

          <div v-if="modalError" class="alert alert-danger text-xs py-2 px-3 mb-3">{{ modalError }}</div>

          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeAssignModal">Cancelar</button>
            <button type="button" class="btn btn-success btn-sm" :disabled="saving" @click="saveAssignment">
              <i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
              {{ editingId ? 'Guardar cambios' : 'Asignar tarea' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import remissionsService from '@/services/remissions.service';
import remissionsDetailsService from '@/services/remissionsDetails.service';
import productionAssignmentsService from '@/services/productionAssignments.service';
import oficiosService from '@/services/oficios.service';

export default {
  name: 'ProductionOrder',
  data() {
    return {
      remissionId: null,
      remission: null,
      details: [],
      assignments: [],
      expanded: {},
      loading: true,
      saving: false,
      users: [],
      oficios: [],
      showAssignModal: false,
      editingId: null,
      modalItem: null,
      modalError: '',
      form: {
        user_id: null,
        oficio_id: null,
        quantity: null,
        price: null,
        assigned_at: new Date().toISOString().slice(0, 10),
        estimated_at: null,
        observation: '',
      },
    };
  },
  computed: {
    totalUnits() {
      return this.details.reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
    },
    totalAssignments() {
      return this.assignments.length;
    },
    completedAssignments() {
      return this.assignments.filter((a) => a.status === 'completed').length;
    },
    totalPay() {
      return this.assignments.reduce((sum, a) => sum + (parseFloat(a.total) || 0), 0);
    },
    formTotal() {
      return (parseFloat(this.form.quantity) || 0) * (parseFloat(this.form.price) || 0);
    },
    modalRemaining() {
      if (!this.modalItem) return 0;
      const assignedOthers = this.itemAssignments(this.modalItem)
        .filter((a) => a.id !== this.editingId)
        .reduce((s, a) => s + (parseFloat(a.quantity) || 0), 0);
      return Math.max(0, parseFloat(this.modalItem.quantity) - assignedOthers);
    },
    /** Usuarios que tienen el oficio seleccionado (campo processes del usuario) */
    filteredUsers() {
      if (!this.form.oficio_id) return [];
      const list = this.users.filter((u) => this.userHasOficio(u, this.form.oficio_id));
      // En edición: incluir siempre al usuario ya asignado aunque ya no tenga el oficio
      if (this.editingId && this.form.user_id && !list.some((u) => u.id === this.form.user_id)) {
        const current = this.users.find((u) => u.id === this.form.user_id);
        if (current) return [...list, current];
      }
      return list;
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('es-ES');
    },
    shortDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('es-ES');
    },
    money(value) {
      return (value || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    preparadoLabel(preparado) {
      switch (parseInt(preparado)) {
        case 1: return 'Preparado';
        case 2: return 'Terminado';
        case 3: return 'En proceso';
        case 4: return 'Fabricado';
        case 5: return 'Despachado';
        default: return 'Pendiente';
      }
    },
    preparadoBadgeClass(preparado) {
      switch (parseInt(preparado)) {
        case 1: return 'bg-info text-dark';
        case 2: return 'bg-success';
        case 3: return 'bg-warning text-dark';
        case 4: return 'bg-primary';
        case 5: return 'bg-secondary';
        default: return 'bg-secondary';
      }
    },
    itemAssignments(item) {
      return this.assignments.filter((a) => a.remission_detail_id === item.id);
    },
    assignedQty(item) {
      return this.itemAssignments(item).reduce((s, a) => s + (parseFloat(a.quantity) || 0), 0);
    },
    completedOf(item) {
      return this.itemAssignments(item).filter((a) => a.status === 'completed').length;
    },
    itemPay(item) {
      return this.itemAssignments(item).reduce((s, a) => s + (parseFloat(a.total) || 0), 0);
    },
    toggleExpand(itemId) {
      this.expanded = { ...this.expanded, [itemId]: !this.expanded[itemId] };
    },
    itemManufacturingStatus(item) {
      return item.manufacturing_status || 'pending';
    },
    statusPillClass(item) {
      const s = this.itemManufacturingStatus(item);
      if (s === 'completed') return 'status-pill--completed';
      if (s === 'in_progress') return 'status-pill--in-progress';
      return 'status-pill--pending';
    },
    assignmentPillClass(a) {
      if (a.status === 'completed') return 'status-pill--completed';
      if (a.status === 'in_progress') return 'status-pill--in-progress';
      return 'status-pill--pending';
    },
    itemSize(item) {
      if (item.variant?.size?.size) return item.variant.size.size;
      if (item.productionModel?.size?.size) return item.productionModel.size.size;
      return '—';
    },
    itemColor(item) {
      if (item.variant?.color?.name) return item.variant.color.name;
      if (item.inventory?.color?.name) return item.inventory.color.name;
      return '—';
    },
    itemStock(item) {
      if (item.available_stock !== undefined && item.available_stock !== null) {
        return item.available_stock;
      }
      if (item.variant) return item.variant.quantity ?? 0;
      return item.inventory?.quantity ?? 0;
    },
    itemBarcode(item) {
      if (item.variant && item.variant.barcode) return item.variant.barcode;
      return item.inventory?.barcode || '—';
    },
    async loadData() {
      this.remissionId = this.$route.query.id;
      if (!this.remissionId) {
        this.loading = false;
        return;
      }
      try {
        const [remRes, detRes, asgRes] = await Promise.all([
          remissionsService.getData(this.remissionId),
          remissionsDetailsService.getData(this.remissionId),
          productionAssignmentsService.getByRemission(this.remissionId),
        ]);
        this.remission = remRes.data;
        this.details = detRes.data || [];
        this.assignments = asgRes.data?.data || [];
      } catch (error) {
        console.error('Error al cargar el pedido:', error);
        await Swal.fire('Error', 'No se pudo cargar el pedido', 'error');
      } finally {
        this.loading = false;
      }
    },
    async loadCatalogs() {
      try {
        const [usersRes, oficiosRes] = await Promise.all([
          this.$store.dispatch('users/getAll', 'all'),
          oficiosService.getOficios(),
        ]);
        this.users = usersRes || [];
        this.oficios = oficiosRes.data || [];
      } catch (error) {
        console.error('Error al cargar usuarios/oficios:', error);
      }
    },
    /** El campo processes del usuario guarda los ids de sus oficios (JSON o array) */
    userHasOficio(user, oficioId) {
      let procs = user.processes;
      if (typeof procs === 'string') {
        try {
          procs = JSON.parse(procs);
        } catch (e) {
          procs = [];
        }
      }
      if (!Array.isArray(procs)) return false;
      return procs.map(String).includes(String(oficioId));
    },
    onOficioChange() {
      // Al cambiar el oficio, el usuario debe pertenecer al nuevo oficio
      if (this.form.user_id && !this.userHasOficio(this.users.find((u) => u.id === this.form.user_id) || {}, this.form.oficio_id)) {
        this.form.user_id = null;
      }
    },
    openAssignModal(item) {
      this.editingId = null;
      this.modalItem = item;
      this.modalError = '';
      this.form = {
        user_id: null,
        oficio_id: null,
        quantity: null,
        price: null,
        assigned_at: new Date().toISOString().slice(0, 10),
        estimated_at: null,
        observation: '',
      };
      this.showAssignModal = true;
      this.expanded = { ...this.expanded, [item.id]: true };
    },
    openEditModal(item, assignment) {
      this.editingId = assignment.id;
      this.modalItem = item;
      this.modalError = '';
      this.form = {
        user_id: assignment.user_id,
        oficio_id: assignment.oficio_id,
        quantity: assignment.quantity,
        price: assignment.price,
        assigned_at: assignment.assigned_at ? String(assignment.assigned_at).slice(0, 10) : null,
        estimated_at: assignment.estimated_at ? String(assignment.estimated_at).slice(0, 10) : null,
        observation: assignment.observation || '',
      };
      this.showAssignModal = true;
    },
    closeAssignModal() {
      this.showAssignModal = false;
      this.modalItem = null;
      this.editingId = null;
      this.modalError = '';
    },
    validateForm() {
      if (!this.form.user_id) return 'Seleccione un usuario.';
      if (!this.form.oficio_id) return 'Seleccione un oficio.';
      if (!this.form.quantity || this.form.quantity <= 0) return 'Ingrese la cantidad.';
      if (this.form.price === null || this.form.price === undefined || this.form.price < 0) return 'Ingrese el precio por unidad.';
      if (this.form.quantity > this.modalRemaining + 0.001) {
        return `La cantidad excede lo disponible del producto (${this.modalRemaining}).`;
      }
      return null;
    },
    async saveAssignment() {
      const error = this.validateForm();
      if (error) {
        this.modalError = error;
        return;
      }
      this.saving = true;
      this.modalError = '';
      try {
        const payload = {
          remission_detail_id: this.modalItem.id,
          user_id: this.form.user_id,
          oficio_id: this.form.oficio_id,
          quantity: this.form.quantity,
          price: this.form.price,
          assigned_at: this.form.assigned_at || null,
          estimated_at: this.form.estimated_at || null,
          observation: this.form.observation || null,
        };
        if (this.editingId) {
          await productionAssignmentsService.updateData(this.editingId, payload);
        } else {
          await productionAssignmentsService.createData(payload);
        }
        const asgRes = await productionAssignmentsService.getByRemission(this.remissionId);
        this.assignments = asgRes.data?.data || [];
        this.closeAssignModal();
        await Swal.fire({
          icon: 'success',
          title: this.editingId ? 'Tarea actualizada' : 'Tarea asignada',
          timer: 1400,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
      } catch (error) {
        this.modalError = error.response?.data?.message || 'No se pudo guardar la tarea';
      } finally {
        this.saving = false;
      }
    },
    async onAssignmentStatusChange(event, assignment) {
      const newStatus = event.target.value;
      const target = event.target;
      if (newStatus === assignment.status) return;
      try {
        const res = await productionAssignmentsService.updateStatus(assignment.id, newStatus);
        const idx = this.assignments.findIndex((a) => a.id === assignment.id);
        if (idx >= 0) {
          this.assignments[idx] = res.data.assignment;
        }
        await Swal.fire({
          icon: 'success',
          title: 'Estado actualizado',
          text: res.data.message,
          timer: 1400,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
      } catch (error) {
        target.value = assignment.status;
        await Swal.fire('Error', error.response?.data?.message || 'No se pudo actualizar el estado', 'error');
      }
    },
    async completeAssignment(assignment) {
      const result = await Swal.fire({
        title: 'Marcar tarea completada',
        html: `¿Marcar como completada la tarea de <b>${assignment.user?.name || 'este usuario'}</b> (${assignment.oficio?.name || 'sin oficio'})? Se registrará la fecha de terminación de hoy.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, completar',
        cancelButtonText: 'Cancelar',
        customClass: { container: 'swal-high-z' },
      });
      if (!result.isConfirmed) return;
      try {
        const res = await productionAssignmentsService.updateStatus(assignment.id, 'completed');
        const idx = this.assignments.findIndex((a) => a.id === assignment.id);
        if (idx >= 0) {
          this.assignments[idx] = res.data.assignment;
        }
        await Swal.fire({
          icon: 'success',
          title: 'Tarea completada',
          timer: 1400,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
      } catch (error) {
        await Swal.fire('Error', error.response?.data?.message || 'No se pudo completar la tarea', 'error');
      }
    },
    async deleteAssignment(assignment) {
      const result = await Swal.fire({
        title: 'Eliminar tarea',
        html: `¿Eliminar la tarea de <b>${assignment.user?.name || 'este usuario'}</b> (${assignment.oficio?.name || 'sin oficio'})?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        customClass: { container: 'swal-high-z' },
      });
      if (!result.isConfirmed) return;
      try {
        await productionAssignmentsService.deleteData(assignment.id);
        this.assignments = this.assignments.filter((a) => a.id !== assignment.id);
        await Swal.fire({
          icon: 'success',
          title: 'Tarea eliminada',
          timer: 1200,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
      } catch (error) {
        await Swal.fire('Error', error.response?.data?.message || 'No se pudo eliminar la tarea', 'error');
      }
    },
    async onManufacturingStatusChange(event, item) {
      const newStatus = event.target.value;
      const current = this.itemManufacturingStatus(item);
      const target = event.target;
      if (newStatus === current) return;

      if (newStatus === 'completed') {
        const result = await Swal.fire({
          title: 'Confirmar fabricado',
          text: `¿Cambiar el estado de "${item.inventory?.name || 'Producto'}" a Fabricado? Se sumarán ${item.quantity} unidades al inventario.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sí, marcar fabricado',
          cancelButtonText: 'Cancelar',
          customClass: { container: 'swal-high-z' },
        });
        if (!result.isConfirmed) {
          target.value = current;
          return;
        }
      }

      try {
        const res = await remissionsDetailsService.updateManufacturingStatus(item.id, newStatus);
        const idx = this.details.findIndex((d) => d.id === item.id);
        if (idx >= 0) {
          const updated = res.data.detail;
          this.details[idx] = {
            ...this.details[idx],
            manufacturing_status: updated.manufacturing_status,
            manufactured: updated.manufactured,
            available_stock: updated.available_stock,
          };
        }
        if (res.data.remission_preparado_changed) {
          this.remission = { ...this.remission, preparado: res.data.remission_preparado };
        }
        await Swal.fire({
          icon: 'success',
          title: 'Estado actualizado',
          text: res.data.message,
          timer: 1500,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
      } catch (error) {
        target.value = current;
        await Swal.fire('Error', error.response?.data?.message || 'No se pudo actualizar el estado', 'error');
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadCatalogs()]);
  },
};
</script>

<style scoped>
.assignment-row > td {
  background: #f8f9fa;
  padding: 0 !important;
}

.assignment-box {
  border-top: 1px dashed #e0e0e0;
}

.picking-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.picking-modal-box {
  width: 600px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
}

.status-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 1.8rem 0.3rem 0.8rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.2;
  outline: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='currentColor' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 10px;
}

.status-select:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.status-select:focus {
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.status-select option {
  font-size: 0.85rem;
  padding: 0.3rem 0.5rem;
  border-radius: 0;
}

.status-pill--pending {
  background: #f3f4f6;
  color: #6b7280;
}

.status-pill--pending:hover {
  background: #e5e7eb;
}

.status-pill--in-progress {
  background: #fef3c7;
  color: #b45309;
}

.status-pill--in-progress:hover {
  background: #fde68a;
}

.status-pill--completed {
  background: #d1fae5;
  color: #047857;
}

.status-pill--completed:hover {
  background: #a7f3d0;
}
</style>

<style>
.swal-high-z {
  z-index: 100000 !important;
}
</style>
